import { countryMap } from '../../../core/utils/constants/constants';
import { transform, isObject } from 'lodash';
import moment from 'moment';

/**
 * Validates if a line item has all required fields for delivery
 * Pure function that can validate any line item object
 * @param {Object} lineItem - The line item object to validate
 * @param {boolean} hideCpmFields - Whether CPM fields should be hidden (Percentage pricing model)
 * @returns {Object} { isValid: boolean, missingFields: string[] }
 */
export const validateLineItemForDelivery = (lineItem, hideCpmFields) => {
  const missingFields = [];

  // 1. Validate Line Item Name
  if (!lineItem.name || lineItem.name.trim() === '') {
    missingFields.push('Line Item Name');
  }

  // 2. Validate Start Date
  if (!lineItem.startDate) {
    missingFields.push('Start Date');
  }

  // 3. Validate End Date
  if (!lineItem.endDate) {
    missingFields.push('End Date');
  }

  // 4. Validate Budget (must be > 0)
  const budget = parseFloat(lineItem.budgetTotal);
  if (!budget || budget <= 0) {
    missingFields.push('Budget');
  }

  // 5. Validate Targeting Type
  if (!lineItem.targetingType) {
    missingFields.push('Targeting Type');
  }

  // 6. Validate CPM (only required for CPM pricing model)
  if (!hideCpmFields) {
    const cpm = parseFloat(lineItem.cpm);
    if (!cpm || cpm < 1) {
      missingFields.push('CPM');
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

export const buildGeoTargetPayload = ({ target, orgId }) => {
  const { circles, polygons, location } = target;

  if (location) {
    delete location.id;
    delete location.__typename;
  }

  return {
    circles: {
      create: circles.length
        ? circles.map((circle) => {
            const circlePayload = {
              radius: circle.radius,
              coordinate: {
                create: {
                  lat: circle.coordinate.lat,
                  lng: circle.coordinate.lng,
                },
              },
            };

            if (circle.location) {
              circlePayload.location = {
                create: {
                  name: circle.location?.name || '',
                  formattedAddress: circle.location?.formattedAddress || '',
                  zip: circle.location?.zip || '',
                  street: circle.location?.street || '',
                  city: circle.location?.city || '',
                  lat: circle.location?.lat || 0,
                  lng: circle.location?.lng || 0,
                  ...circle.location,
                },
              };
            }
            return circlePayload;
          })
        : [],
    },
    polygons: {
      create: polygons.length
        ? polygons.map((polygon) => {
            const polygonPayload = {
              geometry: {
                coordinates: polygon.geometry.coordinates,
              },
            };

            if (polygon.location) {
              polygonPayload.location = {
                create: {
                  name: polygon.location?.name || '',
                  formattedAddress: polygon.location?.formattedAddress || '',
                  zip: polygon.location?.zip || '',
                  street: polygon.location?.street || '',
                  city: polygon.location?.city || '',
                  lat: polygon.location?.lat || 0,
                  lng: polygon.location?.lng || 0,
                  ...polygon.location,
                },
              };
            }

            return polygonPayload;
          })
        : [],
    },
    location: {
      create: {
        name: location?.name || '',
        formattedAddress: location?.formattedAddress || '',
        zip: location?.zip || '',
        street: location?.street || '',
        city: location?.city || '',
        lat: location?.lat || 0,
        lng: location?.lng || 0,
        ...location,
      },
    },
    name: target.name || '',
    start: target.start || null,
    end: target.end || null,
    // orgs: {
    //   connect: {
    //     id: orgId,
    //   },
    // },
    status: 'CREATED',
  };
};

export const buildGeoTargetsPayload = ({ targets = [], originalTargets = [], orgId }) => {
  // Find the targets that were deleted
  const deletedTargets = originalTargets.filter(
    (originalTarget) => !targets.some((target) => target.name === originalTarget.name)
  );

  const newTargets = targets.filter(
    (target) => !originalTargets.some((originalTarget) => originalTarget.name === target.name)
  );

  const payload = {
    create: newTargets.map((target) => buildGeoTargetPayload({ target, orgId })),
  };

  if (deletedTargets.length) {
    payload.delete = deletedTargets.map((target) => ({
      id: target.id,
    }));
  }

  return payload;
};

export const transformLocationToGeoTarget = ({ location }) => {
  const {
    name,
    formattedAddress,
    city,
    state,
    zip,
    lat,
    lng,
    street,
    country,
    geoframe,
    org,
    audienceType,
    circles = [],
    polygons = [],
  } = location;

  const geoFrameCircles = geoframe && geoframe.circles ? geoframe.circles : [];
  const geoFramePolygons = geoframe && geoframe.polygons ? geoframe.polygons : [];

  const newCircles = geoFrameCircles.length ? geoFrameCircles : circles;
  const newPolygons = geoFramePolygons.length ? geoFramePolygons : polygons;

  return {
    circles: newCircles.map((circle) => ({
      ...circle,
      location: {
        name: circle.location ? circle.location.name : name,
        formattedAddress: circle.location ? circle.location.formattedAddress : formattedAddress,
        street: circle.location ? circle.location.street : street,
        state: circle.location ? circle.location.state : state,
        city: circle.location ? circle.location.city : city,
        zip: circle.location ? circle.location.zip : zip,
        country: circle.location ? circle.location.country : country,
        lat: circle.location ? circle.location.lat : lat,
        lng: circle.location ? circle.location.lng : lng,
        audienceType,
      },
    })),
    polygons: newPolygons.map((polygon) => ({
      ...polygon,
      location: {
        name: polygon.location ? polygon.location.name : name,
        formattedAddress: polygon.location ? polygon.location.formattedAddress : formattedAddress,
        street: polygon.location ? polygon.location.street : street,
        state: polygon.location ? polygon.location.state : state,
        city: polygon.location ? polygon.location.city : city,
        zip: polygon.location ? polygon.location.zip : zip,
        country: polygon.location ? polygon.location.country : country,
        lat: polygon.location ? polygon.location.lat : lat,
        lng: polygon.location ? polygon.location.lng : lng,
        audienceType,
      },
    })),
    location: {
      name: name,
      formattedAddress: formattedAddress,
      street: street,
      state: state,
      city: city,
      zip: zip,
      country: country,
      lat: lat,
      lng: lng,
      audienceType,
    },
    name: location.name,
    start: null,
    end: null,
    org,
  };
};

export const buildAdGroupPayload = ({ adGroup, orgId, campaign, originalLineItems = [], forceCreate = false }) => {
  let newAdGroupCreatives = [];
  let updatedAdGroupCreatives = [];
  let deletedAdGroupCreatives = [];

  if (adGroup.adGroupCreatives && adGroup.adGroupCreatives.length) {
    newAdGroupCreatives = adGroup.adGroupCreatives.filter((creative) => !creative.id);
    updatedAdGroupCreatives = adGroup.adGroupCreatives.filter((creative) => creative.id);
  }

  const matchedCampaign = originalLineItems
    .filter(
      (campaign) =>
        campaign.adGroup &&
        campaign.adGroup.adGroupCreatives &&
        campaign.adGroup.adGroupCreatives.length
    )
    .find((c) => c.id === campaign.id);

  if (matchedCampaign) {
    deletedAdGroupCreatives = matchedCampaign.adGroup.adGroupCreatives.filter(
      (creative) => !campaign.adGroup.adGroupCreatives.find((c) => c.name === creative.name)
    );
  }

  if (adGroup && adGroup.id && !forceCreate) {
    return {
      update: {
        clickthrough: adGroup.clickthrough,
        adGroupCreatives: {
          create: newAdGroupCreatives.map((adGroupCreative) => {
            return {
              name: adGroupCreative.name,
              clickthrough: adGroupCreative.clickthrough,
              startDate: adGroupCreative.startDate,
              endDate: adGroupCreative.endDate,
              medias: {
                create: adGroupCreative.medias.map((media) => ({
                  fileType: media.fileType,
                  height: media.height,
                  key: media.key,
                  name: media.name,
                  size: media.size,
                  type: media.type,
                  url: media.url,
                  width: media.width,
                })),
              },
            };
          }),
          update: updatedAdGroupCreatives.map((adGroupCreative) => {
            return {
              where: {
                id: adGroupCreative.id,
              },
              data: {
                startDate: adGroupCreative.startDate,
                endDate: adGroupCreative.endDate,
                clickthrough: adGroupCreative.clickthrough,
              },
            };
          }),
          delete: deletedAdGroupCreatives.map((adGroupCreative) => ({
            id: adGroupCreative.id,
          })),
        },
      },
    };
  } else {
    return {
      create: {
        clickthrough: adGroup.clickthrough,
        adGroupCreatives: {
          create:
            adGroup.adGroupCreatives && adGroup.adGroupCreatives.length
              ? adGroup.adGroupCreatives.map((adGroupCreative) => {
                  const medias = Array.isArray(adGroupCreative.medias)
                    ? adGroupCreative.medias
                    : [];
                  return {
                    name: adGroupCreative.name,
                    clickthrough: adGroupCreative.clickthrough,
                    startDate: adGroupCreative.startDate,
                    endDate: adGroupCreative.endDate,
                    medias: {
                      create: medias.map((media) => ({
                        fileType: media.fileType,
                        height: media.height,
                        key: media.key,
                        mediaHasNotes: media.mediaHasNotes,
                        mediaNotes: media.mediaNotes,
                        name: media.name,
                        org: media.org,
                        size: media.size,
                        type: media.type,
                        url: media.url,
                        width: media.width,
                        sharedWith: {
                          connect: {
                            id: orgId,
                          },
                        },
                      })),
                    },
                  };
                })
              : [],
        },
      },
    };
  }
};

export const calculateImpressions = ({ cpm, budget }) => {
  return budget && cpm ? Math.ceil((Number(budget) / Number(cpm)) * 1000) : 0;
};

export const calculateImpressionsByPerecent = ({ cpm, budget, percent }) => {
  return budget && cpm ? Math.ceil((Number(budget) / Number(cpm)) * 1000 * percent) : 0;
};

export const buildLineItemUpdatePayload = ({ lineItem, originalLineItems, selectedAdvertiser }) => {
  const { targetingRegions, targetingLocations, targetingCountries, audienceType } = lineItem;
  let newTargetingRegions = [];
  let updatedTargetingRegions = [];
  let deletedTargetingRegions = [];
  let newTargetingLocations = [];
  let updatedTargetingLocations = [];
  let deletedTargetingLocations = [];
  let newTargetingCountries = [];
  let deletedTargetingCountries = [];

  switch (lineItem.targetingType) {
    case 'REGION':
      newTargetingRegions = targetingRegions.filter((region) => !region.id);
      updatedTargetingRegions = targetingRegions.filter((region) => region.id);
      deletedTargetingRegions = originalLineItems
        .find((c) => c.id === lineItem.id)
        .targetingRegions.filter((region) => !targetingRegions.find((r) => r.name === region.name));

      // Delete all targeting locations
      deletedTargetingLocations = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingLocations;
      deletedTargetingCountries = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingCountries;

      break;
    case 'LOCAL':
      newTargetingLocations = targetingLocations
        .filter((location) => !location.id)
        .map((location) => {
          return {
            ...location,
            geoTargets: location.geoTargets.map((target) => {
              delete target.id;
              return target;
            }),
          };
        });
      updatedTargetingLocations = targetingLocations.filter((location) => location.id);
      deletedTargetingLocations = originalLineItems
        .find((c) => c.id === lineItem.id)
        .targetingLocations.filter(
          (location) => !targetingLocations.find((l) => l.name === location.name)
        );

      // Delete all targeting regions
      deletedTargetingRegions = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingRegions;
      deletedTargetingCountries = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingCountries;
      break;

    case 'COUNTRY':
      newTargetingCountries = [
        {
          name: countryMap[targetingCountries],
          code: targetingCountries,
        },
      ];

      // Delete all targeting locations
      deletedTargetingLocations = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingLocations;

      // Delete all targeting regions
      deletedTargetingRegions = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingRegions;
      // Delete all targeting countries
      deletedTargetingCountries = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingCountries;
      break;

    default:
      break;
  }

  // Handle audience type
  let newFirstPartyDataAudiences = [];
  let updatedFirstPartyDataAudiences = [];
  let deletedFirstPartyDataAudiences = [];

  switch (audienceType) {
    case 'FIRST_PARTY_DATA':
      newFirstPartyDataAudiences = lineItem.firstPartyDataAudiences.filter(
        (audience) => !audience.id
      );
      updatedFirstPartyDataAudiences = lineItem.firstPartyDataAudiences.filter(
        (audience) => audience.id
      );
      deletedFirstPartyDataAudiences = originalLineItems
        .find((c) => c.id === lineItem.id)
        .firstPartyDataAudiences.filter(
          (audience) => !lineItem.firstPartyDataAudiences.find((a) => a.name === audience.name)
        );

      break;
    case 'ALL_USERS':
      // Delete all first party data audiences
      deletedFirstPartyDataAudiences = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).firstPartyDataAudiences;

      break;
    default:
      break;
  }

  // Handle Deal IDs
  let newDealIds = [];
  let deletedDealIds = [];

  const originalLineItem = originalLineItems.find((c) => c.id === lineItem.id);
  if (lineItem.dealIds && lineItem.dealIds.length) {
    newDealIds = lineItem.dealIds.filter((deal) =>
      !originalLineItem?.dealIds?.find((d) => d.id === deal.id)
    );
  }
  if (originalLineItem?.dealIds && originalLineItem.dealIds.length) {
    deletedDealIds = originalLineItem.dealIds.filter(
      (deal) => !lineItem.dealIds?.find((d) => d.id === deal.id)
    );
  }

  return {
    where: {
      id: lineItem.id,
    },
    data: {
      name: lineItem.name,
      startDate: lineItem.startDate,
      endDate: lineItem.endDate,
      budgetTotal: Number(lineItem.budgetTotal),
      cpm: Number(lineItem.cpm),
      // Launch Details fields
      supplySidePlatform: lineItem.supplySidePlatform || null,
      maxBid: lineItem.maxBid ? Number(lineItem.maxBid) : null,
      frequencyCap: lineItem.frequencyCap ? Number(lineItem.frequencyCap) : null,
      // Handle Deal IDs
      dealIds: {
        connect: newDealIds.map((deal) => ({ id: deal.id })),
        disconnect: deletedDealIds.map((deal) => ({ id: deal.id })),
      },
      // Handle Targeting
      targetingType: lineItem.targetingType,
      valueAdd: lineItem.valueAdd,
      targetingRegions: {
        create: newTargetingRegions.map((region) => ({
          name: region.name,
          regions: {
            set: region.regions.map((region) => region),
          },
        })),
        update: updatedTargetingRegions.map((region) => ({
          where: {
            id: region.id,
          },
          data: {
            name: region.name,
            regions: {
              set: region.regions.map((region) => region),
            },
          },
        })),
        delete: deletedTargetingRegions.map((region) => ({
          id: region.id,
        })),
      },
      targetingLocations: {
        create: newTargetingLocations.map((location) => ({
          name: location.name,
          type: location.type,
          geoTargets: buildGeoTargetsPayload({
            targets: location.geoTargets,
            originalTargets: [],
            orgId: selectedAdvertiser.id,
          }),
        })),
        update: updatedTargetingLocations.map((location) => ({
          where: {
            id: location.id,
          },
          data: {
            name: location.name,
            type: location.type,
            geoTargets: buildGeoTargetsPayload({
              targets: location.geoTargets,
              originalTargets: lineItem.targetingLocations.find((l) => l.name === location.name)
                .geoTargets,
              orgId: selectedAdvertiser.id,
            }),
          },
        })),
        delete: deletedTargetingLocations.map((location) => ({
          id: location.id,
        })),
      },
      targetingCountries: targetingCountries
        ? {
            create: newTargetingCountries.map((country) => ({
              name: country.name,
              code: country.code,
            })),
            delete: deletedTargetingCountries.map((country) => ({
              id: country.id,
            })),
          }
        : null,
      // Handle Audiences
      audienceType: lineItem.audienceType,
      firstPartyDataAudiences: {
        create: newFirstPartyDataAudiences.map((audience) => ({
          name: audience.name,
          activated: audience.activated,
          tag: audience.tag,
          deviceCount: audience.deviceCount,
          matchRate: audience.matchRate,
          isProcessedByStatera: audience.isProcessedByStatera,
          dspSegmentIds: {
            set: audience.dspSegmentIds,
          },
        })),
        update: updatedFirstPartyDataAudiences.map((audience) => ({
          where: {
            id: audience.id,
          },
          data: {
            name: audience.name,
            activated: audience.activated,
            tag: audience.tag,
            deviceCount: audience.deviceCount,
            matchRate: audience.matchRate,
            isProcessedByStatera: audience.isProcessedByStatera,
            dspSegmentIds: {
              set: audience.dspSegmentIds,
            },
          },
        })),
        delete: deletedFirstPartyDataAudiences.map((audience) => ({
          id: audience.id,
        })),
      },
      // Handle Ad Group
      adGroup: lineItem.adGroup
        ? buildAdGroupPayload({
            adGroup: lineItem.adGroup,
            orgId: selectedAdvertiser.id,
            campaign: lineItem,
            originalLineItems: originalLineItems,
          })
        : null,
    },
  };
};

export const buildLineItemPayload = ({ lineItem, originalLineItems, selectedAdvertiser, forceCreate = false }) => {
  const { targetingType } = lineItem;
  const adGroup = lineItem.adGroup;

  const newTargetingCountries = [];

  if (lineItem.targetingCountries) {
    newTargetingCountries.push({
      name: countryMap[lineItem.targetingCountries],
      code: lineItem.targetingCountries,
    });
  }

  return {
    name: lineItem.name,
    startDate: lineItem.startDate,
    endDate: lineItem.endDate,
    budgetTotal: Number(lineItem.budgetTotal),
    cpm: Number(lineItem.cpm),
    // Launch Details fields
    supplySidePlatform: lineItem.supplySidePlatform || null,
    maxBid: lineItem.maxBid ? Number(lineItem.maxBid) : null,
    frequencyCap: lineItem.frequencyCap ? Number(lineItem.frequencyCap) : null,
    // Deal IDs - connect to existing deal IDs
    dealIds: lineItem.dealIds && lineItem.dealIds.length
      ? { connect: lineItem.dealIds.map((deal) => ({ id: deal.id })) }
      : undefined,
    targetingType,
    valueAdd: lineItem.valueAdd,
    targetingRegions: {
      create:
        targetingType === 'REGION'
          ? (lineItem.targetingRegions || []).map((region) => ({
              name: region.name,
              regions: {
                set: (region.regions || []).map((region) => region),
              },
            }))
          : [],
    },
    targetingCountries: {
      create:
        targetingType === 'COUNTRY'
          ? newTargetingCountries.map((country) => ({
              name: country.name,
              code: country.code,
            }))
          : [],
    },
    targetingLocations: {
      create:
        targetingType === 'LOCAL'
          ? (lineItem.targetingLocations || []).map((location) => ({
              name: location.name,
              type: location.type,
              geoTargets: buildGeoTargetsPayload({
                targets: location.geoTargets || [],
                originalTargets: [],
                orgId: selectedAdvertiser.id,
              }),
            }))
          : [],
    },
    audienceType: lineItem.audienceType,
    firstPartyDataAudiences: {
      create: (lineItem.firstPartyDataAudiences || []).map((audience) => ({
        name: audience.name,
        activated: audience.activated,
        tag: audience.tag,
        deviceCount: audience.deviceCount,
        matchRate: audience.matchRate,
        isProcessedByStatera: audience.isProcessedByStatera,
        dspSegmentIds: {
          set: audience.dspSegmentIds,
        },
      })),
    },
    adGroup: adGroup
      ? buildAdGroupPayload({
          adGroup,
          orgId: selectedAdvertiser.id,
          campaign: lineItem,
          originalLineItems: originalLineItems,
          forceCreate: forceCreate,
        })
      : null,
    adUnits: {
      create: [],
    },
  };
};

export const deleteProperties = ({ object, properties }) => {
  return transform(object, (result, value, key) => {
    if (!properties.includes(key)) {
      // Skip properties in the `properties` array
      // Recursively process nested objects or arrays
      result[key] = isObject(value) ? deleteProperties({ object: value, properties }) : value;
    }
  });
};

// Helper: Validates campaign order level fields only (without campaigns)
const validateCampaignOrderFields = (currentCampaignOrder, message) => {
  const { budget, cpm, startDate, endDate, category, advertiser, campaigns } = currentCampaignOrder;

  // Check if advertiser has a dspAdvertiserId
  if (!advertiser.dataConfig.dspAdvertiserId) {
    message.error('Advertiser must have a DSP advertiser ID. Please check organization settings.');
    return false;
  }

  // Check if budget exists and is a valid positive number
  if (!budget || budget <= 0 || isNaN(budget)) {
    message.error('Campaign order budget must be a valid positive number greater than 0');
    return false;
  }

  // Check if CPM exists and is a valid positive number
  if (!cpm || cpm <= 0 || isNaN(cpm)) {
    message.error('Campaign order CPM must be a valid positive number greater than 0');
    return false;
  }

  // Check if start date exists and is valid
  if (!startDate) {
    message.error('Campaign order start date is required');
    return false;
  }

  // Check if end date exists and is valid
  if (!endDate) {
    message.error('Campaign order end date is required');
    return false;
  }

  // Check if start date is before end date
  if (moment(startDate).isSameOrAfter(moment(endDate))) {
    message.error('Campaign order start date must be before end date. Please update dates in Campaign Requester → Campaign Order Details.');
    return false;
  }

  // Check if category exists (can be array or single value)
  if (!category || (Array.isArray(category) && category.length === 0)) {
    message.error('Campaign order category is required');
    return false;
  }

  // Check if advertiser exists
  if (!advertiser) {
    message.error('Campaign order advertiser is required');
    return false;
  }

  // Check if campaigns exist (campaigns ARE the line items in this data structure)
  if (!campaigns || !Array.isArray(campaigns) || campaigns.length === 0) {
    message.error('Campaign order must have at least one line item');
    return false;
  }

  return true;
};

// Validates a specific campaign for launch (campaign order fields + single campaign)
export const validateCampaignForLaunch = (currentCampaignOrder, campaignIndex, message) => {

  // First validate campaign order level fields
  if (!validateCampaignOrderFields(currentCampaignOrder, message)) {
    return false;
  }

  const { campaigns } = currentCampaignOrder;

  // Validate campaign index is valid
  if (campaignIndex < 0 || campaignIndex >= campaigns.length) {
    message.error('Invalid campaign index');
    return false;
  }

  const campaign = campaigns[campaignIndex];

  if (!campaign) {
    message.error(`Campaign ${campaignIndex + 1} is missing`);
    return false;
  }

  // Check if campaign has lineItems array (new nested structure)
  const allLineItems = campaign.lineItems || [campaign];
  const hasNestedStructure = campaign.lineItems && Array.isArray(campaign.lineItems);
  // Filter to only include line items marked for delivery
  const lineItems = allLineItems.filter((lineItem) => lineItem.markedForDelivery);
  // Check if there are any line items marked for delivery
  if (lineItems.length === 0) {
    message.error(`No line items are marked for delivery in campaign ${campaignIndex + 1}`);
    return false;
  }

  // Validate each line item within this specific campaign (only those marked for delivery)
  for (let j = 0; j < lineItems.length; j++) {
    const lineItem = lineItems[j];
    // Find the original index for the label
    const originalIndex = allLineItems.findIndex((li) => li === lineItem);
    const lineItemLabel = hasNestedStructure ? `line item ${originalIndex + 1} in campaign ${campaignIndex + 1}` : `line item ${campaignIndex + 1}`;

    if (!lineItem) {
      message.error(`Line item is missing for ${lineItemLabel}`);
      return false;
    }

    // Check if line item name is provided
    if (!lineItem.name || lineItem.name.trim() === '') {
      message.error(`Name is required for ${lineItemLabel}`);
      return false;
    }

    // Check if ad group exists
    if (!lineItem.adGroup) {
      message.error(`Ad Group is required for ${lineItemLabel}`);
      return false;
    }

    // Check if ad group has creatives
    if (!lineItem.adGroup.adGroupCreatives || !Array.isArray(lineItem.adGroup.adGroupCreatives) || lineItem.adGroup.adGroupCreatives.length === 0) {
      message.error(`Ad Group must have at least one creative for ${lineItemLabel}`);
      return false;
    }

    // Validate each ad group creative
    for (let k = 0; k < lineItem.adGroup.adGroupCreatives.length; k++) {
      const creative = lineItem.adGroup.adGroupCreatives[k];

      if (!creative) {
        message.error(`Creative ${k + 1} is missing in ad group for ${lineItemLabel}`);
        return false;
      }

      // Check if creative name is provided
      if (!creative.name || creative.name.trim() === '') {
        message.error(`Creative name is required for creative ${k + 1} in ${lineItemLabel}`);
        return false;
      }

      // Check if clickthrough URL is provided
      if (!creative.clickthrough || creative.clickthrough.trim() === '') {
        message.error(`Clickthrough URL is required for creative ${k + 1} in ${lineItemLabel}`);
        return false;
      }

      // Check if creative has medias
      if (!creative.medias || !Array.isArray(creative.medias) || creative.medias.length === 0) {
        message.error(`Creative must have at least one media for creative ${k + 1} in ${lineItemLabel}`);
        return false;
      }
    }

    // Check if start date exists and is valid
    if (!lineItem.startDate) {
      message.error(`Start date is required for ${lineItemLabel}`);
      return false;
    }

    // Check if end date exists and is valid
    if (!lineItem.endDate) {
      message.error(`End date is required for ${lineItemLabel}`);
      return false;
    }

    // Check if line item start date is before end date
    if (moment(lineItem.startDate).isSameOrAfter(moment(lineItem.endDate))) {
      message.error(`Start date must be before end date for ${lineItemLabel}. Please update dates in Campaign Requester → Line Items.`);
      return false;
    }

    // Check if line item CPM is a valid positive number
    if (!lineItem.cpm || lineItem.cpm <= 0 || isNaN(lineItem.cpm)) {
      message.error(`CPM must be a valid positive number for ${lineItemLabel}`);
      return false;
    }

    // Check if line item budget is a valid positive number (using budgetTotal)
    if (!lineItem.budgetTotal || lineItem.budgetTotal <= 0 || isNaN(lineItem.budgetTotal)) {
      message.error(`Budget must be a valid positive number for ${lineItemLabel}`);
      return false;
    }

    // Validate flight schedule budgets sum matches line item budget
    // Check singular first: form edits write to "flightSchedule" (singular),
    // while GraphQL returns "flightSchedules" (plural) which may be stale after edits.
    const flightSchedules = lineItem.flightSchedule || lineItem.flightSchedules || [];
    if (flightSchedules.length > 0) {
      const flightBudgetTotal = flightSchedules.reduce((sum, flight) => {
        const budget = Number(flight.budget);
        return sum + (isNaN(budget) ? 0 : budget);
      }, 0);
      const lineItemBudget = Number(lineItem.budgetTotal);
      // Use small epsilon for floating point comparison
      if (Math.abs(flightBudgetTotal - lineItemBudget) > 0.01) {
        message.error(
          `Flight schedule total budget ($${flightBudgetTotal.toFixed(2)}) does not match line item budget ($${lineItemBudget.toFixed(2)}) for ${lineItemLabel}. Please adjust flight schedule budgets.`
        );
        return false;
      }
    }

    // Check if targeting type exists
    if (!lineItem.targetingType) {
      message.error(`Targeting type is required for ${lineItemLabel}`);
      return false;
    }

    // Check if targeting data exists based on targeting type
    if (lineItem.targetingType === 'REGION' && (!lineItem.targetingRegions || lineItem.targetingRegions.length === 0)) {
      message.error(`Targeting regions are required for ${lineItemLabel}`);
      return false;
    }

    if (lineItem.targetingType === 'LOCAL' && (!lineItem.targetingLocations || lineItem.targetingLocations.length === 0)) {
      message.error(`Targeting locations are required for ${lineItemLabel}`);
      return false;
    }

    if (lineItem.targetingType === 'COUNTRY' && (!lineItem.targetingCountries || lineItem.targetingCountries.length === 0)) {
      message.error(`Targeting countries are required for ${lineItemLabel}`);
      return false;
    }

  }

  return true;
};

/**
 * Validates all dates across campaign order hierarchy for launch readiness
 * Checks dates at all levels: Campaign Order, Campaign, Line Items, Flight Schedules
 *
 * For Programmatic Launch:
 * - Validates all START dates >= today
 * - Validates all END dates >= START dates
 *
 * For Manual Launch:
 * - Skips START date validation (campaigns may already be live in DSP)
 * - Only validates END dates >= START dates
 *
 * @param {Object} currentCampaignOrder - The campaign order object
 * @param {number} campaignIndex - Index of campaign to validate
 * @param {string} launchType - 'programmatic' or 'manual'
 * @returns {Object} { isValid: boolean, errors: Array<DateError> }
 */
export const validateAllDatesForLaunch = (currentCampaignOrder, campaignIndex, launchType) => {
  const errors = [];
  const today = moment().startOf('day');
  const isProgrammatic = launchType === 'programmatic';

  // 1. Validate Campaign Order dates
  if (currentCampaignOrder.startDate && currentCampaignOrder.endDate) {
    const coStartDate = moment(currentCampaignOrder.startDate).startOf('day');
    const coEndDate = moment(currentCampaignOrder.endDate).startOf('day');

    // For programmatic: check if start date is in the past
    if (isProgrammatic && coStartDate.isBefore(today)) {
      errors.push({
        level: 'CAMPAIGN_ORDER',
        entityName: 'Campaign Order',
        field: 'startDate',
        date: currentCampaignOrder.startDate,
        formattedDate: moment(currentCampaignOrder.startDate).format('MM/DD/YYYY'),
        message: `Campaign Order start date (${moment(currentCampaignOrder.startDate).format('MM/DD/YYYY')}) is in the past`
      });
    }

    // For both: check if end date < start date
    if (coEndDate.isBefore(coStartDate)) {
      errors.push({
        level: 'CAMPAIGN_ORDER',
        entityName: 'Campaign Order',
        field: 'endDate',
        date: currentCampaignOrder.endDate,
        formattedDate: moment(currentCampaignOrder.endDate).format('MM/DD/YYYY'),
        message: `Campaign Order end date (${moment(currentCampaignOrder.endDate).format('MM/DD/YYYY')}) is before start date (${moment(currentCampaignOrder.startDate).format('MM/DD/YYYY')})`
      });
    }
  }

  // 2. Validate Campaign dates
  const campaign = currentCampaignOrder.campaigns[campaignIndex];
  if (campaign) {
    if (campaign.startDate && campaign.endDate) {
      const campaignStartDate = moment(campaign.startDate).startOf('day');
      const campaignEndDate = moment(campaign.endDate).startOf('day');

      // For programmatic: check if start date is in the past
      if (isProgrammatic && campaignStartDate.isBefore(today)) {
        errors.push({
          level: 'CAMPAIGN',
          entityName: campaign.name || `Campaign ${campaignIndex + 1}`,
          field: 'startDate',
          date: campaign.startDate,
          formattedDate: moment(campaign.startDate).format('MM/DD/YYYY'),
          message: `Campaign "${campaign.name || `Campaign ${campaignIndex + 1}`}" start date (${moment(campaign.startDate).format('MM/DD/YYYY')}) is in the past`
        });
      }

      // For both: check if end date < start date
      if (campaignEndDate.isBefore(campaignStartDate)) {
        errors.push({
          level: 'CAMPAIGN',
          entityName: campaign.name || `Campaign ${campaignIndex + 1}`,
          field: 'endDate',
          date: campaign.endDate,
          formattedDate: moment(campaign.endDate).format('MM/DD/YYYY'),
          message: `Campaign "${campaign.name || `Campaign ${campaignIndex + 1}`}" end date (${moment(campaign.endDate).format('MM/DD/YYYY')}) is before start date (${moment(campaign.startDate).format('MM/DD/YYYY')})`
        });
      }
    }

    // 3. Validate Line Item dates (only those marked for delivery)
    if (campaign.lineItems && Array.isArray(campaign.lineItems)) {
      campaign.lineItems.forEach((lineItem, lineItemIndex) => {
        // Only validate line items marked for delivery
        if (lineItem.markedForDelivery && lineItem.startDate && lineItem.endDate) {
          const lineItemStartDate = moment(lineItem.startDate).startOf('day');
          const lineItemEndDate = moment(lineItem.endDate).startOf('day');
          const lineItemLabel = lineItem.name || `Line Item ${lineItemIndex + 1}`;

          // For programmatic: check if start date is in the past
          if (isProgrammatic && lineItemStartDate.isBefore(today)) {
            errors.push({
              level: 'LINE_ITEM',
              entityName: lineItemLabel,
              lineItemIndex,
              field: 'startDate',
              date: lineItem.startDate,
              formattedDate: moment(lineItem.startDate).format('MM/DD/YYYY'),
              message: `Line Item "${lineItemLabel}" start date (${moment(lineItem.startDate).format('MM/DD/YYYY')}) is in the past`
            });
          }

          // For both: check if end date < start date
          if (lineItemEndDate.isBefore(lineItemStartDate)) {
            errors.push({
              level: 'LINE_ITEM',
              entityName: lineItemLabel,
              lineItemIndex,
              field: 'endDate',
              date: lineItem.endDate,
              formattedDate: moment(lineItem.endDate).format('MM/DD/YYYY'),
              message: `Line Item "${lineItemLabel}" end date (${moment(lineItem.endDate).format('MM/DD/YYYY')}) is before start date (${moment(lineItem.startDate).format('MM/DD/YYYY')})`
            });
          }

          // 4. Validate Flight Schedule dates (if present)
          // Check both singular (form state) and plural (GraphQL) field names
          const flightSchedules = lineItem.flightSchedule || lineItem.flightSchedules || [];
          if (Array.isArray(flightSchedules) && flightSchedules.length > 0) {
            flightSchedules.forEach((flight, flightIndex) => {
              if (flight.startDate && flight.endDate) {
                const flightStartDate = moment(flight.startDate).startOf('day');
                const flightEndDate = moment(flight.endDate).startOf('day');

                // For programmatic: check if start date is in the past
                if (isProgrammatic && flightStartDate.isBefore(today)) {
                  errors.push({
                    level: 'FLIGHT_SCHEDULE',
                    entityName: `Flight ${flightIndex + 1}`,
                    lineItemIndex,
                    lineItemName: lineItemLabel,
                    flightIndex,
                    field: 'startDate',
                    date: flight.startDate,
                    formattedDate: moment(flight.startDate).format('MM/DD/YYYY'),
                    message: `Flight Schedule ${flightIndex + 1} in "${lineItemLabel}" has start date (${moment(flight.startDate).format('MM/DD/YYYY')}) in the past`
                  });
                }

                // For both: check if end date < start date
                if (flightEndDate.isBefore(flightStartDate)) {
                  errors.push({
                    level: 'FLIGHT_SCHEDULE',
                    entityName: `Flight ${flightIndex + 1}`,
                    lineItemIndex,
                    lineItemName: lineItemLabel,
                    flightIndex,
                    field: 'endDate',
                    date: flight.endDate,
                    formattedDate: moment(flight.endDate).format('MM/DD/YYYY'),
                    message: `Flight Schedule ${flightIndex + 1} in "${lineItemLabel}" has end date (${moment(flight.endDate).format('MM/DD/YYYY')}) before start date (${moment(flight.startDate).format('MM/DD/YYYY')})`
                  });
                }
              }
            });
          }
        }
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Pre-flight validation function to check campaign order fields and ALL campaigns
export const validateCampaignOrder = (currentCampaignOrder, message) => {
  const { campaigns } = currentCampaignOrder;

  // First validate campaign order level fields
  if (!validateCampaignOrderFields(currentCampaignOrder, message)) {
    return false;
  }

  // Validate each campaign (which are actually line items)
  for (let i = 0; i < campaigns.length; i++) {
    const campaign = campaigns[i];

    if (!campaign) {
      message.error(`Campaign ${i + 1} is missing`);
      return false;
    }

    // Check if campaign has lineItems array (new nested structure)
    const lineItems = campaign.lineItems || [campaign];
    const hasNestedStructure = campaign.lineItems && Array.isArray(campaign.lineItems);

    // Validate each line item within the campaign
    for (let j = 0; j < lineItems.length; j++) {
      const lineItem = lineItems[j];
      const lineItemLabel = hasNestedStructure ? `line item ${j + 1} in campaign ${i + 1}` : `line item ${i + 1}`;

      if (!lineItem) {
        message.error(`Line item is missing for ${lineItemLabel}`);
        return false;
      }

      // Check if line item name is provided
      if (!lineItem.name || lineItem.name.trim() === '') {
        message.error(`Name is required for ${lineItemLabel}`);
        return false;
      }

      // Check if ad group exists
      if (!lineItem.adGroup) {
        message.error(`Ad Group is required for ${lineItemLabel}`);
        return false;
      }

      // Check if ad group has creatives
      if (!lineItem.adGroup.adGroupCreatives || !Array.isArray(lineItem.adGroup.adGroupCreatives) || lineItem.adGroup.adGroupCreatives.length === 0) {
        message.error(`Ad Group must have at least one creative for ${lineItemLabel}`);
        return false;
      }

      // Validate each ad group creative
      for (let k = 0; k < lineItem.adGroup.adGroupCreatives.length; k++) {
        const creative = lineItem.adGroup.adGroupCreatives[k];

        if (!creative) {
          message.error(`Creative ${k + 1} is missing in ad group for ${lineItemLabel}`);
          return false;
        }

        // Check if creative name is provided
        if (!creative.name || creative.name.trim() === '') {
          message.error(`Creative name is required for creative ${k + 1} in ${lineItemLabel}`);
          return false;
        }

        // Check if clickthrough URL is provided
        if (!creative.clickthrough || creative.clickthrough.trim() === '') {
          message.error(`Clickthrough URL is required for creative ${k + 1} in ${lineItemLabel}`);
          return false;
        }

        // Check if creative has medias
        if (!creative.medias || !Array.isArray(creative.medias) || creative.medias.length === 0) {
          message.error(`Creative must have at least one media for creative ${k + 1} in ${lineItemLabel}`);
          return false;
        }
      }

      // Check if start date exists and is valid
      if (!lineItem.startDate) {
        message.error(`Start date is required for ${lineItemLabel}`);
        return false;
      }

      // Check if end date exists and is valid
      if (!lineItem.endDate) {
        message.error(`End date is required for ${lineItemLabel}`);
        return false;
      }

      // Check if line item start date is before end date
      if (moment(lineItem.startDate).isSameOrAfter(moment(lineItem.endDate))) {
        message.error(`Start date must be before end date for ${lineItemLabel}. Please update dates in Campaign Requester → Line Items.`);
        return false;
      }

      // Check if line item CPM is a valid positive number
      if (!lineItem.cpm || lineItem.cpm <= 0 || isNaN(lineItem.cpm)) {
        message.error(`CPM must be a valid positive number for ${lineItemLabel}`);
        return false;
      }

      // Check if line item budget is a valid positive number (using budgetTotal)
      if (!lineItem.budgetTotal || lineItem.budgetTotal <= 0 || isNaN(lineItem.budgetTotal)) {
        message.error(`Budget must be a valid positive number for ${lineItemLabel}`);
        return false;
      }

      // Check if targeting type exists
      if (!lineItem.targetingType) {
        message.error(`Targeting type is required for ${lineItemLabel}`);
        return false;
      }

      // Check if targeting data exists based on targeting type
      if (lineItem.targetingType === 'REGION' && (!lineItem.targetingRegions || lineItem.targetingRegions.length === 0)) {
        message.error(`Targeting regions are required for ${lineItemLabel}`);
        return false;
      }

      if (lineItem.targetingType === 'LOCAL' && (!lineItem.targetingLocations || lineItem.targetingLocations.length === 0)) {
        message.error(`Targeting locations are required for ${lineItemLabel}`);
        return false;
      }

      if (lineItem.targetingType === 'COUNTRY' && (!lineItem.targetingCountries || lineItem.targetingCountries.length === 0)) {
        message.error(`Targeting countries are required for ${lineItemLabel}`);
        return false;
      }
    }
  }
  
  return true;
};

/**
 * Comprehensive cleaning function for line items during cloning operations
 * Recursively removes database IDs and references while preserving user data
 */
export const cleanLineItemsForCloning = (campaignOrder) => {

  if (!campaignOrder || !campaignOrder.campaigns || !campaignOrder.campaigns.length) {
    return campaignOrder;
  }

  const cleanedCampaignOrder = { ...campaignOrder };

  // Clean campaigns array
  cleanedCampaignOrder.campaigns = campaignOrder.campaigns.map((campaign, campaignIndex) => {

    const cleanedCampaign = { ...campaign };

    // Remove campaign-level database fields
    delete cleanedCampaign.id;
    delete cleanedCampaign.createdAt;
    delete cleanedCampaign.updatedAt;
    delete cleanedCampaign.__typename;
    delete cleanedCampaign.adProviderId;

    // Clean line items
    if (campaign.lineItems && campaign.lineItems.length > 0) {

      cleanedCampaign.lineItems = campaign.lineItems.map((lineItem, lineItemIndex) => {

        const cleanedLineItem = { ...lineItem };

        // Remove line item database fields
        delete cleanedLineItem.id;
        delete cleanedLineItem.createdAt;
        delete cleanedLineItem.updatedAt;
        delete cleanedLineItem.__typename;

        // Remove DSP-specific fields (should not be cloned)
        delete cleanedLineItem.dspLineItemId;
        delete cleanedLineItem.adProviderId;

        // Exclude cloning-specific fields to use GraphQL schema defaults
        delete cleanedLineItem.isOrigin;
        delete cleanedLineItem.markedForDelivery;
        delete cleanedLineItem.valueAdd;

        // Clean targeting regions
        if (cleanedLineItem.targetingRegions && Array.isArray(cleanedLineItem.targetingRegions)) {
          cleanedLineItem.targetingRegions = cleanedLineItem.targetingRegions.map(region => {
            const cleanedRegion = { ...region };
            delete cleanedRegion.id;
            delete cleanedRegion.createdAt;
            delete cleanedRegion.updatedAt;
            delete cleanedRegion.__typename;
            return cleanedRegion;
          });
        }

        // Clean targeting locations
        if (cleanedLineItem.targetingLocations && Array.isArray(cleanedLineItem.targetingLocations)) {
          cleanedLineItem.targetingLocations = cleanedLineItem.targetingLocations.map(location => {
            const cleanedLocation = { ...location };
            delete cleanedLocation.id;
            delete cleanedLocation.createdAt;
            delete cleanedLocation.updatedAt;
            delete cleanedLocation.__typename;

            // Clean nested geo targets
            if (cleanedLocation.geoTargets && Array.isArray(cleanedLocation.geoTargets)) {
              cleanedLocation.geoTargets = cleanedLocation.geoTargets.map(geoTarget => {
                const cleanedGeoTarget = { ...geoTarget };
                delete cleanedGeoTarget.id;
                delete cleanedGeoTarget.__typename;

                // Clean circles
                if (cleanedGeoTarget.circles && Array.isArray(cleanedGeoTarget.circles)) {
                  cleanedGeoTarget.circles = cleanedGeoTarget.circles.map(circle => {
                    const cleanedCircle = { ...circle };
                    delete cleanedCircle.id;
                    delete cleanedCircle.__typename;

                    if (cleanedCircle.location) {
                      delete cleanedCircle.location.id;
                      delete cleanedCircle.location.__typename;
                    }
                    if (cleanedCircle.coordinate) {
                      delete cleanedCircle.coordinate.id;
                      delete cleanedCircle.coordinate.__typename;
                    }
                    return cleanedCircle;
                  });
                }

                // Clean polygons
                if (cleanedGeoTarget.polygons && Array.isArray(cleanedGeoTarget.polygons)) {
                  cleanedGeoTarget.polygons = cleanedGeoTarget.polygons.map(polygon => {
                    const cleanedPolygon = { ...polygon };
                    delete cleanedPolygon.id;
                    delete cleanedPolygon.__typename;

                    if (cleanedPolygon.location) {
                      delete cleanedPolygon.location.id;
                      delete cleanedPolygon.location.__typename;
                    }
                    return cleanedPolygon;
                  });
                }

                // Clean location
                if (cleanedGeoTarget.location) {
                  delete cleanedGeoTarget.location.id;
                  delete cleanedGeoTarget.location.__typename;
                }

                return cleanedGeoTarget;
              });
            }

            return cleanedLocation;
          });
        }

        // Clean targeting countries
        if (cleanedLineItem.targetingCountries && Array.isArray(cleanedLineItem.targetingCountries)) {
          cleanedLineItem.targetingCountries = cleanedLineItem.targetingCountries.map(country => {
            const cleanedCountry = { ...country };
            delete cleanedCountry.id;
            delete cleanedCountry.__typename;
            return cleanedCountry;
          });
        }

        // Clean first party data audiences
        if (cleanedLineItem.firstPartyDataAudiences && Array.isArray(cleanedLineItem.firstPartyDataAudiences)) {
          cleanedLineItem.firstPartyDataAudiences = cleanedLineItem.firstPartyDataAudiences.map(audience => {
            const cleanedAudience = { ...audience };
            delete cleanedAudience.id;
            delete cleanedAudience.createdAt;
            delete cleanedAudience.updatedAt;
            delete cleanedAudience.__typename;

            // Clean hashed emails
            if (cleanedAudience.hashedEmails && Array.isArray(cleanedAudience.hashedEmails)) {
              cleanedAudience.hashedEmails = cleanedAudience.hashedEmails.map(email => {
                const cleanedEmail = { ...email };
                delete cleanedEmail.id;
                delete cleanedEmail.__typename;
                return cleanedEmail;
              });
            }

            return cleanedAudience;
          });
        }

        // Clean deal IDs
        if (cleanedLineItem.dealIds && Array.isArray(cleanedLineItem.dealIds)) {
          cleanedLineItem.dealIds = cleanedLineItem.dealIds.map(deal => {
            const cleanedDeal = { ...deal };
            delete cleanedDeal.id;
            delete cleanedDeal.__typename;

            if (cleanedDeal.org) {
              delete cleanedDeal.org.id;
              delete cleanedDeal.org.__typename;
            }

            return cleanedDeal;
          });
        }

        // Clean ad group and creatives (critical for both display and video)
        if (cleanedLineItem.adGroup) {
          const cleanedAdGroup = { ...cleanedLineItem.adGroup };
          delete cleanedAdGroup.id;
          delete cleanedAdGroup.createdAt;
          delete cleanedAdGroup.updatedAt;
          delete cleanedAdGroup.__typename;

          // Clean ad group creatives
          if (cleanedAdGroup.adGroupCreatives && Array.isArray(cleanedAdGroup.adGroupCreatives)) {

            cleanedAdGroup.adGroupCreatives = cleanedAdGroup.adGroupCreatives.map((creative, creativeIndex) => {

              const cleanedCreative = { ...creative };
              delete cleanedCreative.id;
              delete cleanedCreative.createdAt;
              delete cleanedCreative.updatedAt;
              delete cleanedCreative.__typename;
              // Remove DSP-specific fields (should not be cloned)
              delete cleanedCreative.adProviderId;

              // Clean media files
              if (cleanedCreative.medias && Array.isArray(cleanedCreative.medias)) {
                cleanedCreative.medias = cleanedCreative.medias.map(media => {
                  const cleanedMedia = { ...media };
                  delete cleanedMedia.id;
                  delete cleanedMedia.createdAt;
                  delete cleanedMedia.updatedAt;
                  delete cleanedMedia.__typename;
                  // Remove DSP-specific fields (should not be cloned)
                  delete cleanedMedia.dspCreativeId;
                  delete cleanedMedia.dspAdId;
                  return cleanedMedia;
                });
              }

              return cleanedCreative;
            });
          }

          cleanedLineItem.adGroup = cleanedAdGroup;
        }

        return cleanedLineItem;
      });

    } else {
      cleanedCampaign.lineItems = [];
    }

    return cleanedCampaign;
  });


  return cleanedCampaignOrder;
};
